/* THE DISPLAY RULE FOR A VOCABULARY KEY THE GENERATED TABLE DOES NOT ANSWER.
 *
 * 🔴 IT LIVES IN ITS OWN FILE BECAUSE `equipment.ts` IS GENERATED AND CANNOT HOLD IT. That file's
 * first line says `GENERATED FILE — DO NOT EDIT`, and a harness ruler regenerates it in memory on
 * every run and fails if a single byte differs. The emitter `return`s at `EQUIPMENT_COUNT`, so
 * ANYTHING appended below it — however well commented — makes the comparison impossible to
 * satisfy. This helper was appended there on 2026-09-18 and the ruler has been red ever since;
 * nobody saw it because the next full fixture suite did not run until that night.
 *
 * ⚠️ THE LESSON IS NOT "DO NOT EDIT GENERATED FILES", WHICH EVERYBODY ALREADY KNOWS. It is that a
 * generated file with a HAND-WRITTEN TAIL looks like a file with two halves and is actually a file
 * that can never pass its own check — and the comment announcing the split reads like permission.
 * One file per author: the machine owns `equipment.ts`, a person owns this.
 */
import { CANONICAL_MUSCLES } from "./muscles";
import { EQUIPMENT_LABEL } from "./equipment";

/* WHY IT IS NEEDED, MEASURED against the live library on 2026-09-18: of the 31 equipment keys
   the 142 `coach_exercises` rows actually use, 29 are in `EQUIPMENT_LABEL` and TWO are not —
   `weight_belt` and `yoga_block` — so a chip row printed "Barbell" beside a raw "weight_belt".
   `primary_muscles` is worse and is FREE TEXT by design (see `muscles.ts`): 39 distinct
   spellings, mixing "Hip & Groin", "hipAbductors" and "quadriceps" in one column.

   ⚠️ THE KEY REMAINS THE VALUE. This changes what is DRAWN, never what is stored or sent, and
   the caller keeps its own `is-unknown` flag — a value the engine does not recognise is still
   marked as such, it is merely marked legibly.

   🔴 THE SENTENCE THAT USED TO END THIS COMMENT WAS WRONG AND IS REPLACED BY THE MEASUREMENT
   (2026-09-18). It read: "closing the two-key gap properly means adding them to the Swift enum
   and regenerating, which is a catalogue change and its own commit." That was a HYPOTHESIS
   written beside a measurement, and it travelled — into the record, then into a session brief
   as an instruction. Both halves are false:
     * `weight_belt` ALREADY resolves. `EquipmentNormalizer` carries `"weight belt": .dipBelt`
       as an exact synonym key, and `CanonicalEquipment.dipBelt` is ALREADY displayed as
       "Weight Belt" (his 2026-09-05 word; the raw value stays so stored rows resolve). A
       `weightBelt` enum case would be ONE OBJECT UNDER TWO NAMES.
     * `yoga_block` resolves to `.raisedSurface`, and an enum case for it would have been dead
       on arrival: `canonical(for:)` tries phrase CONTAINMENT before the direct rawValue match,
       and `"block"` is already a key — so the new case could never be reached.
   What was actually missing was INTENT and a CHECK, and both now exist in the engine: a stated
   `"yoga block"` synonym key, and executed assertions in the `the-equipment-audit` gate that
   resolve both spellings on every run with a fail-closed control. This file is unchanged in
   behaviour; `vocabularyLabel` still draws the label and the key is still the value. */

export function vocabularyLabel(raw: string): string {
  const known = EQUIPMENT_LABEL[raw];
  if (known !== undefined) return known;
  const spaced = raw.replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
  if (spaced === "") return raw;
  // Title Case per word, because that is the register the generated table is written in — a
  // fallback that reads "Weight belt" beside "Barbell" still looks like a bug to the coach.
  return spaced.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/* ── Muscles ─────────────────────────────────────────────────────────────────────────────────── */

/**
 * How a stored muscle is DRAWN.
 *
 * 🔴 THIS FUNCTION ONCE ALSO CLAIMED WHETHER THE ENGINE COULD READ THE SPELLING, AND THAT CLAIM
 * WAS WRONG — RETRACTED THE SAME HOUR, BY RENDERING IT (2026-09-18). `canonical` was computed
 * against `CANONICAL_MUSCLES`, which is the ENUM’S CASES ONLY: 24 keys and 24 labels. The Swift
 * side’s `MuscleNormalizer` also carries a SYNONYM table — that is its entire job, and this repo
 * names it as the fix for the muscle vocabulary mismatch. So `Shoulders`, `Chest`, `Core` and
 * `Full Body` all came back `canonical: false`, and the table drew **146 chips** on one screen
 * each carrying a tooltip saying the engine counts it for nothing. Whether that is even true of
 * `Shoulders` is exactly what this file cannot answer.
 * ⚠️ A COLUMN WHERE HALF THE CHIPS CARRY A WARNING IS NOISE, AND A CONFIDENTLY WRONG WARNING IS
 * WORSE THAN NONE — a coach would go and "fix" spellings that were never broken.
 * The flag is kept because the LABEL half is real and useful, and because the honest version of
 * the readability claim needs `MuscleNormalizer`’s synonym table GENERATED into the web the way
 * `equipment.ts` already is. Until then nothing renders the flag. Filed, not forgotten.
 *
 * What it still does: turns a stored spelling into something a coach can read — `hipGroin` into
 * `Hip Groin`, `latissimusDorsi` into `Lats`.
 *
 * 🔴 THE SECOND HALF IS THE POINT, AND §6.7'S "OR EVEN BETTER" IN ONE CELL.
 * `coach_exercises.primary_muscles` is free text carrying **39 distinct spellings** for about
 * twenty muscles (`muscles.ts` lists them, measured 2026-09-18). `MuscleNormalizer` copes with the
 * known ones at read time; a spelling it does not know is a muscle the relatedness score and the
 * coverage reads see as NOTHING — and nothing anywhere turns red. A coach looking at their own
 * library is the only person who could notice, and until now the screen showed them `hipGroin`
 * beside `Quadriceps` with no way to tell that one of the two is invisible to the engine.
 *
 * ⚠️ IT NEVER CORRECTS. Guessing that `hipGroin` means `adductors` would be this screen inventing
 * a fact about somebody else's exercise.
 *
 * ⚠️ `canonical` NOW MEANS ONLY "this is one of the 24 enum cases, by key or label". It is not a
 * statement about what the engine can read, and no screen may draw it as one until the synonym
 * table is generated into the web.
 */
export function muscleLabel(raw: string): { label: string; canonical: boolean } {
  const t = raw.trim().toLowerCase();
  const hit = CANONICAL_MUSCLES.find((m) => m.key.toLowerCase() === t || m.label.toLowerCase() === t);
  if (hit !== undefined) return { label: hit.label, canonical: true };
  return { label: vocabularyLabel(raw), canonical: false };
}
